import {
  AccountBalance,
  ArrowDropUp,
  AttachMoney,
  Business,
  CheckCircle,
  CheckCircleOutline,
  Checklist,
  Circle,
  ContentCopy,
  DoneAll,
  Edit,
  Email,
  Error,
  Info,
  OpenInNew,
  Phone,
  PriceCheck,
  Report,
  SmartToyOutlined,
} from '@mui/icons-material';
import FolderIcon from '@mui/icons-material/Folder';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SwapHorizontalCircleOutlinedIcon from '@mui/icons-material/SwapHorizontalCircleOutlined';
import {
  alpha,
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Requirement } from 'lib/api/requirements/types';
import { DecimalInput } from 'lib/common/Inputs';
import { RESSELL_CONCILIATION_TYPES_DESCRIPTIONS } from 'lib/consts';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { BASE_COLORS } from 'lib/functions/const/colors';
import {
  formatDate,
  formatMoney,
  formatMoneyUF,
  getBestTextColorFromHex,
  validatePermissions,
} from 'lib/functions/utils';
import { companySizes } from 'pages/BusinessProfile/Profile/components/Client/components/constants';
import React from 'react';

import EnhancedTable from '.';

export const formatDateToLocal = (value: any, short = true) => {
  const dateObj = new Date(value);

  const date = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
  const time = `${String(dateObj.getHours()).padStart(2, '0')}:${String(dateObj.getMinutes()).padStart(2, '0')}:${String(dateObj.getSeconds()).padStart(2, '0')}`;
  if (short) return `${date}`;

  return `${date} ${time}`;
};

export const colors: { [key: string]: string } = {
  'NO SOLICITADA': '#D7D7D7', // Grey
  Creada: '#3bc14a', // Green
  Cotizada: BASE_COLORS.orange, // Blue
  Revisada: '#30a14e', // A different shade of green
  'En revisión': '#E8C547', // Yellow
  Cedida: '#2d9cdb', // A different shade of blue
  Aceptada: '#34d058', // Another shade of green, slightly lighter
  Aprobado: '#28a745', // A more distinct green
  Aprobada: '#28a745', // A more distinct green
  'Faltan datos': '#dbab09', // Darker yellow
  Cancelada: '#D62839', // Red
  Gestionada: '#2563eb', // Another shade of blue, deeper
  Confirmada: '#2185d0', // Blue, slightly different
  Rechazada: BASE_COLORS.red, // A different shade of red
  Rechazado: BASE_COLORS.red, // A different shade of red
  Fallido: BASE_COLORS.red, // A different shade of red
  Finalizada: '#2c974b', // Another green, darker
  Advertencia: '#ffc107', // Amber
  Recomprada: '#ffc107', // A more distinct green
  Vencida: '#e5534b', // A unique blue
  Validando: '#007bff', // Bright blue
  'No solicitada': '#D7D7D7', // Grey, same as 'NO SOLICITADA' for consistency
  Asignada: '#4dade9', // Light blue
  Activa: '#28a745', // Fresh green
  Congelada: '#82a1b1', // Cold blue-grey
  OPEN: '#f39c12', // Bright orange
  CLOSED: '#27ae60', // Calm green
  Completada: '#D7D7D7', // Bright green
  PENDING: '#E8C547',
  REVIEWED: '#3bc14a',
  ACTIVATED: '#3bc14a',
  DEACTIVATED: '#D62839',
  'En giro': '#007bff',
  'En cartera': '#ffc107',
  IN_TRANSFER: '#007bff',
  Pagado: BASE_COLORS.green,
  Parcial: BASE_COLORS.yellow,
  Pendiente: BASE_COLORS.red,
  Conectado: BASE_COLORS.green,
  'No Conectado': BASE_COLORS.yellow,
  Desconectado: BASE_COLORS.orange,
  'Por Eliminar': BASE_COLORS.red,
  Eliminado: BASE_COLORS.gray,
  Borrador: BASE_COLORS.gray,
  'En espera': BASE_COLORS.yellow,
  'En ejecución': BASE_COLORS.orange,
  Completado: BASE_COLORS.green,
  'Rechazado por notario': BASE_COLORS.red,
  'Rechazado por GoFirmex': BASE_COLORS.red,
  'Rechazado por cliente': BASE_COLORS.red,
  'Rechazado por expiración': BASE_COLORS.red,
  Invalidado: BASE_COLORS.red,
  Abierto: '#27ae60',
  Cerrado: '#f39c12',
  Anulada: BASE_COLORS.red,
  Siniestrada: BASE_COLORS.yellow,
  'Mercado Público': BASE_COLORS.green,
  API: BASE_COLORS.blue,
  Otro: BASE_COLORS.gray,
  Cliente: BASE_COLORS.orange,
  'Mis cuentas': BASE_COLORS.yellow,
  'Nota de crédito': BASE_COLORS.red,
  Centralizado: BASE_COLORS.green,
  'Por Centralizar': BASE_COLORS.yellow,
  'Judicial P': BASE_COLORS.red,
  'Judicial O': BASE_COLORS.red,
  Prejudicial: BASE_COLORS.orange,
  'Cobranza Judicial': BASE_COLORS.yellow,
  Recuperado: BASE_COLORS.green,
  Incobrable: BASE_COLORS.red,
  Castigado: BASE_COLORS.red,
  Perdida: BASE_COLORS.red,
  'No facturado': BASE_COLORS.gray,
  Lead: BASE_COLORS.purple,
  'En Gestión': BASE_COLORS.indigo,
  Contactado: BASE_COLORS.teal,
  'Por Cerrar': BASE_COLORS.amber,
  Transable: '#3bc14a',
  'NC Completa': '#D62839',
  Reclamada: '#D62839',
  Solicitud: '#2563eb',
  'Pago al contado': BASE_COLORS.gray,
  'Cargando XML': BASE_COLORS.blue,
  'Ejecutando cesiones': BASE_COLORS.blue,
  'Cesiones ejecutadas': BASE_COLORS.green,
  'Error al ejecutar cesiones': BASE_COLORS.red,
  Interno: BASE_COLORS.blue,
  Externo: BASE_COLORS.orange,
  Pagada: BASE_COLORS.green,
};

const fsStatusColor: { [key: string]: string } = {
  CREADO: '#E8C547',
  CONFIGURANDO: '#E8C547',
  ANALIZADO: '#0F23DF',
  ASIGNADO: '#3bc14a',
};

const colorsTurn: { [key: string]: string } = {
  'Tu turno': '#E8C547',
  Esperando: '#D7D7D7',
};

const conciliationStatusColor: { [key: string]: string } = {
  Pendiente: '#E8C547',
  Conciliado: '#3bc14a',
};

const debtStatusColor: { [key: string]: string } = {
  Vencida: '#D62839',
  Regulada: '#3bc14a',
  Convenio: '#3bc14a',
  Embargo: '#D62839',
  Pagada: '#3bc14a',
};
const colorsCession: { [key: string]: string } = {
  Transable: '#3bc14a',
  'NC Completa': '#D62839',
  Reclamada: '#D62839',
  'En cartera': '#ffc107',
  Cedida: '#D7D7D7',
  Solicitud: '#2563eb',
  'Pago al contado': '#D7D7D7',
};

const chipsTextColors: { [key: string]: string } = {
  Pagado: '#104600',
  Parcial: '#631d00',
  Pendiente: '#852e07',
  Conectado: 'white',
  'No Conectado': '#631d00',
  Desconectado: 'white',
  'Por Eliminar': 'white',
  Eliminado: 'black',
};

function formatTime(minutes: number): string {
  if (minutes < 60) {
    return `${Math.floor(minutes)} M`;
  } else if (minutes < 1440) {
    // Menos de un día (24 horas * 60 minutos)
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    return `${hours} H${hours > 1 ? 's' : ''} ${mins} M`;
  } else {
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    // const mins = minutes % 60;
    return `${days} día${days > 1 ? 's' : ''} ${hours} hora${hours !== 1 ? 's' : ''}`;
  }
}

const normalizeDate = (date: number) => {
  const dateNormalized = new Date(date);
  dateNormalized.setUTCHours(12, 0, 0, 0);
  return dateNormalized.getTime();
};

const getArrearFromSegments = (document: any, operationDate: any) => {
  const paymentDate = document.paymentDate || document.dueDate;
  const diffDays =
    (normalizeDate(new Date(operationDate).getTime()) -
      normalizeDate(new Date(paymentDate).getTime())) /
    (1000 * 60 * 60 * 24);
  const arrearSegments = document.arrearStructure
    ? document.arrearStructure.arrearSegments
    : document.documentArrearStructure
      ? document.documentArrearStructure.arrearSegments
      : [];
  const arrearSegment = arrearSegments.find(
    (segment: { min: number; max: number; value: number }) =>
      diffDays >= segment.min && diffDays <= segment.max,
  );
  return arrearSegment ? `${arrearSegment.value.toFixed(2)}%` : '0.00%';
};

const disableEditArrearStructure = (
  document: any,
  operationDate: any,
  user: any,
) => {
  if (!validatePermissions(user, 'financing_update_arrear_rate')) {
    return true;
  }
  return document.conciliationLog.length > 0
    ? document.conciliationLog[document.conciliationLog.length - 1].date >=
        operationDate
    : false;
};

export const cellFormat = (
  value: any,
  type?: any,
  canNull = false,
  nullValue?: string,
  handleAction?: any,
  rowId?: any,
  rowData?: any,
  customAction?: (rowId: string | number, value?: any) => void,
  extraData?: any,
  formatter?: (value: Date, format?: string) => string,
) => {
  if ((!type && !nullValue) || (value === null && !canNull && !nullValue))
    return value;

  let customValue = value;
  if (nullValue) {
    customValue = value === null ? nullValue : value;
  }

  if (customValue === 'EMPTY-CELL') return null;

  switch (type) {
    case 'cession-lights':
      if (customValue === 'No cedido')
        return (
          <Tooltip title={'No cedido'}>
            <Circle
              style={{
                color: '#E8C547',
                fontWeight: 'bold',
              }}
            />
          </Tooltip>
        );
      if (isNaN(new Date(customValue).getTime()))
        return (
          <Tooltip title={customValue}>
            <Circle
              style={{
                color: '#D62839',
                fontWeight: 'bold',
              }}
            />
          </Tooltip>
        );
      return (
        <Tooltip title={formatDate(customValue)}>
          <Circle
            style={{
              color: '#3bc14a',
              fontWeight: 'bold',
            }}
          />
        </Tooltip>
      );
    case 'cession-lights-false':
      if (customValue) {
        return (
          <Tooltip title={formatDate(customValue)}>
            <Circle
              style={{
                color: '#D62839',
                fontWeight: 'bold',
              }}
            />
          </Tooltip>
        );
      } else {
        return null;
      }
    case 'cancel-lights':
      return (
        <Tooltip
          title={customValue ? cellFormat(customValue, 'date') : 'Sin fecha'}
        >
          <Circle
            style={{
              color: !customValue ? '#3bc14a' : '#e5534b',
              fontWeight: 'bold',
            }}
          />
        </Tooltip>
      );
    case 'cancel-lights-with-credit-note-amount':
      return (
        <Tooltip
          title={
            customValue
              ? cellFormat(customValue, 'date-with-info')
              : 'Sin fecha'
          }
        >
          <Circle
            style={{
              color: !customValue ? '#3bc14a' : '#e5534b',
              fontWeight: 'bold',
            }}
          />
        </Tooltip>
      );
    case 'confirm-lights':
      return (
        <Tooltip
          title={customValue ? cellFormat(customValue, 'date') : 'Sin fecha'}
        >
          <Circle
            style={{
              color: customValue ? '#3bc14a' : '#E8C547',
              fontWeight: 'bold',
            }}
          />
        </Tooltip>
      );
    case 'paid-lights':
      return (
        <Tooltip
          title={customValue ? cellFormat(customValue, 'date') : 'No pagada'}
        >
          <Circle
            style={{
              color: customValue ? '#3bc14a' : '#D62839',
              fontWeight: 'bold',
            }}
          />
        </Tooltip>
      );
    case 'yes-no-circle':
      return (
        <Circle
          style={{
            color: customValue ? '#3bc14a' : '#E8C547',
            fontWeight: 'bold',
          }}
        />
      );
    case 'decision':
      return (
        <Chip
          label={customValue}
          style={{
            backgroundColor: colors[customValue],
            color: 'white',
            fontWeight: 'bold',
          }}
        />
      );
    case 'due':
      if (customValue)
        return (
          <Chip
            label="Vencida"
            style={{
              backgroundColor: '#D62839',
              color: 'white',
              fontWeight: 'bold',
            }}
          />
        );
      return (
        <Chip
          label="Pagada"
          style={{
            backgroundColor: '#3bc14a',
            color: 'white',
            fontWeight: 'bold',
          }}
        />
      );
    case 'due-relaxed':
      if (customValue)
        return (
          <Chip
            label="Vencida"
            style={{
              backgroundColor: '#D62839',
              color: 'white',
              fontWeight: 'bold',
            }}
          />
        );
      return <p></p>;
    case 'debt-status':
      return (
        <Chip
          label={customValue}
          style={{
            backgroundColor: debtStatusColor[customValue],
            color: 'white',
            fontWeight: 'bold',
            opacity: 1,
          }}
        />
      );
    case 'days':
      return customValue !== 1 ? `${customValue} días` : `${customValue} día`;
    case 'checked':
      if (customValue) return <CheckCircle style={{ color: '#3bc14a' }} />;
      return <Error style={{ color: '#D62839' }} />;
    case 'vission':
      if (customValue)
        return (
          <CheckCircle
            style={{ color: '#0F23DF', textAlign: 'center', paddingLeft: 30 }}
          />
        );
      return null;
    case 'status':
      return (
        <Chip
          label={customValue}
          style={{
            backgroundColor: colors[customValue],
            color: 'white',
            fontWeight: 'bold',
            opacity: 1,
          }}
        />
      );
    case 'fs-status':
      return (
        <Chip
          label={customValue}
          style={{
            backgroundColor: fsStatusColor[customValue],
            color: 'white',
            fontWeight: 'bold',
            opacity: 1,
          }}
        />
      );
    case 'cession':
      return (
        <Chip
          label={customValue}
          style={{
            backgroundColor: colorsCession[customValue],
            color: 'white',
            fontWeight: 'bold',
          }}
        />
      );
    case 'conciliation-status':
      return (
        <Chip
          label={customValue}
          style={{
            backgroundColor: conciliationStatusColor[customValue],
            color: 'white',
            fontWeight: 'bold',
          }}
        />
      );
    case 'formatMinutes':
      return formatTime(customValue);
    case 'boolean-active-null':
      return customValue ? (
        <div style={{ color: '#3bc14a', display: 'flex', textAlign: 'right' }}>
          <CheckCircleOutline style={{ marginRight: '0px' }} />
        </div>
      ) : null;
    case 'boolean-exception-null':
      return customValue ? (
        <LightTooltip
          title={
            <div style={{ padding: '12px' }}>
              <Typography>
                Está solicitud no utiliza línea ya que es una <b>Excepción</b>.
              </Typography>
            </div>
          }
          placement="top"
        >
          <Report color="warning" style={{ cursor: 'pointer' }} />
        </LightTooltip>
      ) : null;
    case 'boolean-active':
      return customValue ? (
        <div
          style={{ color: '#3bc14a', display: 'flex', alignItems: 'center' }}
        >
          <CheckCircleOutline style={{ marginRight: '5px' }} />
          <b>Activo</b>
        </div>
      ) : (
        <div
          style={{ color: '#D62839', display: 'flex', alignItems: 'center' }}
        >
          <HighlightOffIcon style={{ marginRight: '5px' }} />
          <b>Inactivo</b>
        </div>
      );
    case 'numeric-id':
      return Number(customValue) < 0 ? `P-${-customValue}` : `${customValue}`;
    case 'documents-confirmed-light':
      switch (customValue) {
        case 'all':
          return (
            <div
              style={{
                color: '#3bc14a',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <CheckCircle style={{ marginRight: '5px' }} />
              <b>Todos</b>
            </div>
          );
        case 'some':
          return (
            <div
              style={{
                color: '#E8C547',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Error style={{ marginRight: '5px' }} />
              <b>Algunos</b>
            </div>
          );
        case 'none':
          return (
            <div
              style={{
                color: '#FF5F1F',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Error style={{ marginRight: '5px' }} />
              <b>Ninguno</b>
            </div>
          );
        default:
          return (
            <div
              style={{
                color: '#D62839',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Error style={{ marginRight: '5px' }} />
              <b>Tiene rechazos</b>
            </div>
          );
      }
    case 'verificationStatus':
      switch (customValue) {
        case 'Verificada':
          return (
            <div
              style={{
                color: '#3bc14a',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <CheckCircle style={{ marginRight: '5px' }} />
              <b>Verificada</b>
            </div>
          );
        case 'Rechazada':
          return (
            <div
              style={{
                color: '#D62839',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Error style={{ marginRight: '5px' }} />
              <b>Rechazada</b>
            </div>
          );
        default:
          return (
            <div
              style={{
                color: '#E8C547',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Error style={{ marginRight: '5px' }} />
              <b>Pendiente</b>
            </div>
          );
      }
    case 'turn':
      return (
        <Chip
          label={customValue}
          style={{
            backgroundColor: colorsTurn[customValue],
            color: 'white',
            fontWeight: 'bold',
          }}
        />
      );
    case 'sii-connection':
      return (
        <Chip
          label={customValue}
          size="small"
          style={{
            backgroundColor: colors[customValue],
            color: chipsTextColors[customValue],
            fontWeight: 'bold',
            opacity: 1,
            width: '100px',
            minWidth: '100px',
          }}
        />
      );
    case 'sii-connection-temporary': {
      const color = customValue ? BASE_COLORS.green : BASE_COLORS.yellow;
      const label = customValue ? 'Conectado' : 'No Conectado';
      return (
        <Chip
          label={label}
          style={{
            backgroundColor: alpha(color, 0.2),
            color: color,
            fontWeight: 'bold',
            fontSize: '12px',
            opacity: 1,
            width: '110px',
          }}
        />
      );
    }

    case 'boolean-yes': {
      const color = customValue ? BASE_COLORS.green : BASE_COLORS.red;
      const label = customValue ? 'Si' : 'No';
      return (
        <Chip
          label={label}
          style={{
            backgroundColor: alpha(color, 0.2),
            color: color,
            fontWeight: 'bold',
            opacity: 1,
            width: '100px',
          }}
        />
      );
    }
    case 'expiry-date-colored': {
      const status = new Date(value) < new Date() ? 'En mora' : 'Pagada';
      return (
        <span style={{ color: status === 'En mora' ? 'red' : 'inherit' }}>
          {formatDate(value)}
        </span>
      );
    }
    case 'NCCancelDate':
      return customValue ? (
        <span style={{ color: 'red' }}>{customValue}</span>
      ) : (
        '$0 - No'
      );
    case 'date':
      if (!customValue) return '';
      if (formatter) {
        return formatter(customValue);
      }
      return formatDate(customValue);
    case 'eight-days-date': {
      if (!customValue) return '';
      const referenceDate = new Date(customValue);
      const hasPassedEightDays =
        referenceDate < new Date(Date.now() - 9 * 24 * 60 * 60 * 1000);
      return hasPassedEightDays ? (
        <span>
          {formatDate(customValue, 'dd-mm-yyyy')}{' '}
          <DoneAll style={{ fontSize: 14, color: '#3bc14a' }} />
        </span>
      ) : (
        formatDate(customValue, 'dd-mm-yyyy')
      );
    }
    case 'date-with-info':
      return customValue
        ? `${formatDate(customValue.split('%&%')[0])} (${customValue.split('%&%')[1]})`
        : 'Sin información';
    case 'due-date': {
      const today = new Date();
      const dueDate = new Date(customValue);
      let color;
      if (dueDate > today) {
        color = '#3bc14a'; // Verde para al día
      } else if (formatDate(dueDate) === formatDate(today)) {
        color = '#E8C547'; // Amarillo para hoy
      } else {
        color = '#D62839'; // Rojo para vencido
      }

      return (
        <Chip
          label={formatDate(customValue)}
          style={{
            backgroundColor: color,
            color: 'white',
            fontWeight: 'bold',
          }}
        />
      );
    }
    case 'date-colored':
      return (
        <Chip
          label={formatDate(customValue, 'dd-mm-yyyy')}
          style={{
            backgroundColor: alpha('#E8C547', 0.2),
            color: '#E8C547',
            fontWeight: 'bold',
          }}
        />
      );
    case 'date-colored-ok':
      return (
        <Chip
          label={formatDate(customValue, 'dd-mm-yyyy')}
          style={{
            backgroundColor: alpha('#3bc14a', 0.2),
            color: '#3bc14a',
            fontWeight: 'bold',
          }}
        />
      );
    case 'date-large':
      return formatDateToLocal(customValue, false);
    case 'money':
      return formatMoney(customValue);
    case 'good-money':
      return (
        <span
          style={{
            color: '#3bc14a',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ArrowDropUp />
          {formatMoney(customValue)}
        </span>
      );
    case 'money-K':
      return formatMoney(parseInt(customValue) * 1000);
    case 'money-M':
      return `${formatMoney(parseInt(customValue) / 1000000)}M`;
    case 'money-uf':
      return formatMoneyUF(customValue);
    case 'money-colored': {
      const color = customValue === 0 ? BASE_COLORS.gray : BASE_COLORS.red;
      return (
        <Box sx={{ width: '100%' }}>
          <Chip
            label={formatMoney(customValue)}
            sx={{
              width: '100%',
              backgroundColor: alpha(color, 0.2),
              color: color,
              fontWeight: 'bold',
              '& .MuiChip-label': { justifyContent: 'center' },
            }}
          />
        </Box>
      );
    }
    case 'ratio':
      return isNaN(parseFloat(customValue))
        ? customValue
        : parseFloat(customValue).toFixed(2);
    case 'date-hours':
      return formatDate(customValue);
    case 'percentage':
      return `${(customValue * 100).toFixed(2)}%`;
    case 'percentage-soft':
      return customValue ? `${customValue}%` : '0%';
    case 'collection-invoice-status':
      return (
        <Chip
          label={customValue === 'OPEN' ? 'En proceso' : 'Cerrada'}
          style={{
            backgroundColor: colors[customValue],
            color: 'white',
            fontWeight: 'bold',
          }}
        />
      );
    case 'status-chip': {
      const color = colors[customValue] ?? BASE_COLORS.gray;
      return (
        <Chip
          label={customValue}
          style={{
            backgroundColor: alpha(color, 0.2),
            color: color,
            fontWeight: 'bold',
            fontSize: '12px',
            opacity: 1,
            width: '110px',
          }}
        />
      );
    }
    case 'collection-type':
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {customValue === 'CALL' && (
            <>
              <Phone style={{ marginRight: 5 }} /> Llamada{' '}
            </>
          )}
          {customValue === 'EMAIL' && (
            <>
              <Email style={{ marginRight: 5 }} /> Correo{' '}
            </>
          )}
          {customValue === 'CLOSED' && (
            <>
              <PriceCheck style={{ marginRight: 5 }} /> Pago completado{' '}
            </>
          )}
          {['auto-enrollment', 'auto-unenrollment'].includes(customValue) && (
            <>
              <SmartToyOutlined style={{ marginRight: 5 }} /> Cobranza
              Automática{' '}
            </>
          )}
          {![
            'CALL',
            'EMAIL',
            'CLOSED',
            'auto-enrollment',
            'auto-unenrollment',
          ].includes(customValue) && customValue}
        </span>
      );
    case 'invoice-type':
      return (
        <>
          {customValue === 'cession' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <SwapHorizontalCircleOutlinedIcon
                style={{ marginRight: '5px' }}
              />
              <span>Cesión</span>
            </div>
          )}
          {customValue === 'confirming' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <CheckCircle style={{ marginRight: '5px' }} />
              <span>Confirming</span>
            </div>
          )}
          {customValue === 'resell' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <SwapHorizontalCircleOutlinedIcon
                style={{ marginRight: '5px' }}
              />
              <span>Cesión a fondo</span>
            </div>
          )}
          {customValue === 'invoice' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <ReceiptIcon style={{ marginRight: '5px' }} />
              <span>Factura</span>
            </div>
          )}
          {customValue === 'credit' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <AttachMoney style={{ marginRight: '5px' }} />
              <span>Crédito</span>
            </div>
          )}
        </>
      );
    case 'invoice-type-no-icon':
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {customValue === 'cession' && <span>Factoring</span>}
          {customValue === 'invoice' && <span>Factura</span>}
          {customValue === 'credit' && <span>Crédito</span>}
          {customValue === 'resell' && <span>En fondo</span>}
          {customValue === 'confirming' && <span>Confirming</span>}
          {customValue === 'warranty-claim' && <span>SGR</span>}
        </span>
      );
    case 'auto-collection-status':
      return (
        <>
          {customValue && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <SmartToyOutlined style={{ marginRight: '5px' }} />
              <span>Activo</span>
            </div>
          )}
        </>
      );
    case 'bot-generated':
      return customValue ? (
        <Tooltip title="Creado por bot">
          <SmartToyOutlined
            style={{
              color: '#0F23DF',
              cursor: 'pointer',
              fontSize: '20px',
            }}
          />
        </Tooltip>
      ) : (
        <Tooltip title="Creado manualmente">
          <Circle
            style={{
              color: '#D7D7D7',
              cursor: 'pointer',
              fontSize: '16px',
            }}
          />
        </Tooltip>
      );
    case 'pn-invoices':
      return (
        <ul>
          {customValue.map((invoice: any, index: number) => (
            <li key={index}># {invoice.folio}</li>
          ))}
        </ul>
      );
    case 'pn-business':
      return customValue.name;
    case 'pn-status':
      return (
        <Chip
          label={
            customValue === 'PENDING'
              ? 'Pendiente'
              : customValue === 'REVIEWED'
                ? 'Revisada'
                : customValue
          }
          style={{
            backgroundColor: colors[customValue],
            color: 'white',
            fontWeight: 'bold',
          }}
        />
      );
    case 'isFather':
      return (
        <span style={{ fontSize: 16 }}>
          <b>{customValue}</b>
        </span>
      );
    case 'paid':
      return (
        <Chip
          label={customValue ? 'Pagada' : 'Pendiente'}
          style={{
            backgroundColor: customValue ? '#3bc14a' : '#E8C547',
            color: 'white',
            fontWeight: 'bold',
          }}
        />
      );
    case 'invoice-remaining-amount':
      return (
        <>
          {customValue > 0 && (
            <Chip
              label={formatMoney(customValue)}
              style={{
                backgroundColor: '#E8C547',
                color: 'white',
                fontWeight: 'bold',
              }}
            />
          )}
          {customValue === 0 && (
            <Chip
              label="Factura pagada"
              style={{
                backgroundColor: '#3bc14a',
                color: 'white',
                fontWeight: 'bold',
              }}
            />
          )}
        </>
      );
    case 'alert': {
      const color =
        customValue === 'Peligro' || customValue === 'Mega peligro'
          ? BASE_COLORS.red
          : customValue === 'Advertencia'
            ? BASE_COLORS.yellow
            : BASE_COLORS.green;
      return <Circle sx={{ color: color }} />;
    }
    case 'report':
      return (
        <div style={{ padding: '7.5px 0px' }}>
          <span>{customValue}</span>
        </div>
      );
    case 'documentAlert':
      if (customValue === 'creditNote') {
        return 'ceca';
      } else if (customValue === 'cessionReceived') {
        return 'Recepción de cesión';
      } else if (customValue === 'canceledInvoice') {
        return 'Factura anulada';
      }
      return customValue;
    case 'collectionManagementStateName':
      return (
        <Chip
          label={customValue['name']}
          style={{
            color: 'black',
            fontWeight: 'bold',
          }}
        />
      );
    case 'chip':
      return (
        <Chip
          label={customValue}
          style={{
            backgroundColor: '#3bc14a',
            color: 'white',
            fontWeight: 'bold',
          }}
        />
      );
    case 'array-names':
      if (Array.isArray(customValue)) {
        if (
          customValue.length > 0 &&
          typeof customValue[0] === 'object' &&
          customValue[0] !== null &&
          'name' in customValue[0]
        ) {
          return customValue.map((item) => item.name).join(', ');
        }
        return customValue.join(', ');
      }
      return customValue;
    case 'sales-segment':
      return (
        companySizes.find((size) => size.id === customValue)?.name ||
        customValue
      );
    case 'arrear-trench':
      switch (customValue) {
        case 'Sin mora':
        case 'Vigente':
          return (
            <div
              style={{
                color: '#3bc14a',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <CheckCircle style={{ marginRight: '5px' }} />
              <b>{customValue}</b>
            </div>
          );
        case '1 a 30 días':
        case '31 a 60 días':
          return (
            <div
              style={{
                color: '#E8C547',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Error style={{ marginRight: '5px' }} />
              <b>{customValue}</b>
            </div>
          );
        default:
          return (
            <div
              style={{
                color: '#D62839',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Error style={{ marginRight: '5px' }} />
              <b>{customValue}</b>
            </div>
          );
      }

    case 'dual-text':
      if (
        customValue &&
        typeof customValue === 'object' &&
        'primary' in customValue &&
        'secondary' in customValue
      ) {
        const threshhold = customValue.maxStringLength ?? 20;
        const firstValue = customValue.primary ?? '';
        const secondValue = customValue.secondary ?? '';
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              padding: '2px 0',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.875rem',
                fontWeight: 400,
                color: 'text.primary',
                lineHeight: 1.2,
              }}
            >
              {firstValue.length < threshhold
                ? firstValue
                : `${firstValue.slice(0, threshhold)}...`}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 400,
                color: 'text.secondary',
                lineHeight: 1.2,
              }}
            >
              {secondValue.length < threshhold
                ? secondValue
                : `${secondValue.slice(0, threshhold)}...`}
            </Typography>
          </Box>
        );
      }
      return null;
    case 'text-date':
      if (
        customValue &&
        typeof customValue === 'object' &&
        customValue.text &&
        customValue.date
      ) {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              padding: '2px 0',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.875rem',
                fontWeight: 400,
                color: 'text.primary',
                lineHeight: 1.2,
              }}
            >
              {customValue.text}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 400,
                color: 'text.secondary',
                lineHeight: 1.2,
              }}
            >
              {formatDate(customValue.date)}
            </Typography>
          </Box>
        );
      }
      return customValue;
    case 'payment-date':
      if (
        customValue &&
        typeof customValue === 'object' &&
        customValue.amount !== undefined &&
        customValue.amount !== null
      ) {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              padding: '2px 0',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.875rem',
                fontWeight: 400,
                color: 'text.primary',
                lineHeight: 1.2,
              }}
            >
              {formatMoney(customValue.amount)}
            </Typography>
            {customValue.date && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 400,
                  color: 'text.secondary',
                  lineHeight: 1.2,
                }}
              >
                {formatDate(customValue.date)}
              </Typography>
            )}
          </Box>
        );
      }
      return 'Sin información';
    case 'folder-system':
      if (handleAction) {
        if (customValue.endsWith('/')) {
          return (
            <>
              <IconButton onClick={() => handleAction(rowId)}>
                <FolderIcon fontSize="small" />
              </IconButton>
              {customValue}
            </>
          );
        }
        return (
          <>
            <IconButton onClick={() => handleAction(rowId)}>
              <InsertDriveFileIcon fontSize="small" />
            </IconButton>
            {customValue}
          </>
        );
      }
      return customValue;
    case 'color':
      return (
        <Chip
          label={customValue}
          style={{
            backgroundColor: customValue,
            color: getBestTextColorFromHex(customValue),
            fontWeight: 'bold',
          }}
        />
      );
    case 'management':
      if (!customValue) {
        return <span style={{ color: '#D62839' }}>Sin gestión</span>;
      }
      return <span>{formatDate(customValue)}</span>;
    case 'bold-text':
      return <span style={{ fontWeight: 'bold' }}>{customValue}</span>;
    case 'blacklist-action':
      return (
        <Chip
          label={customValue === 'add' ? 'Agregado' : 'Eliminado'}
          style={{
            backgroundColor: customValue === 'add' ? '#D62839' : '#3bc14a',
            color: 'white',
            fontWeight: 'bold',
          }}
        />
      );
    case 'arrear-structure-editable':
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {customValue.arrearType === 'table' && (
            <LightTooltip
              title={
                <EnhancedTable
                  data={customValue.arrearSegments.map(
                    (segment: any, index: number) => {
                      const min = segment.min;
                      const max = segment.max;
                      const value = segment.value;
                      let name = `${min} a ${max} días`;
                      if (index === customValue.arrearSegments.length - 1) {
                        name = `Mayor a ${min} días`;
                      }
                      return {
                        segment: name,
                        value: `${value}%`,
                      };
                    },
                  )}
                  dataHeaders={[
                    {
                      key: 'segment',
                      label: 'Tramo',
                    },
                    {
                      key: 'value',
                      label: 'Valor',
                    },
                  ]}
                  hideTablePagination
                  rowsPerPageDefault={100}
                />
              }
              placement="top"
            >
              <Info
                style={{
                  cursor: 'pointer',
                  fontSize: 14,
                  color: '#666',
                }}
              />
            </LightTooltip>
          )}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography variant="body1" fontWeight={600}>
              {customValue.arrearType === 'table'
                ? `Tabla (${getArrearFromSegments(rowData, extraData.operationDate)})`
                : `${customValue.arrearRate.toFixed(2)}%`}
            </Typography>
          </div>
          <IconButton
            onClick={() => customAction?.(rowId)}
            disabled={disableEditArrearStructure(
              rowData,
              extraData.operationDate,
              extraData.user,
            )}
          >
            <Edit
              style={{
                cursor: 'pointer',
                fontSize: 14,
              }}
            />
          </IconButton>
        </div>
      );
    case 'editable-arrear-rate':
      return (
        <DecimalInput
          value={
            typeof customValue === 'number'
              ? customValue
              : customValue
                ? Number(customValue)
                : null
          }
          onChange={(newValue) => customAction?.(rowId, newValue)}
          isPercentage
          decimalPlaces={2}
        />
      );
    case 'url': {
      const validUrl = customValue !== '';
      return (
        <IconButton
          onClick={() => {
            if (validUrl)
              window.open(customValue, '_blank', 'noopener,noreferrer');
          }}
          disabled={!validUrl}
          aria-label="Abrir enlace"
        >
          <OpenInNew
            style={{
              color: validUrl ? BASE_COLORS.gray : BASE_COLORS.softGray,
            }}
          />
        </IconButton>
      );
    }
    case 'boolean-chip':
      return (
        <Chip
          label={customValue ? 'Si' : 'No'}
          style={{
            backgroundColor: customValue ? '#3bc14a' : '#D62839',
            color: 'white',
            fontWeight: 'bold',
          }}
        />
      );
    case 'invoice-sub-type':
      switch (customValue) {
        case '33':
          return 'Factura electrónica';
        case '34':
          return 'Factura exenta';
        case '46':
          return 'Factura de compra';
      }
      return customValue;
    case 'is-finance-company': {
      const color = customValue ? BASE_COLORS.purple : BASE_COLORS.orange;
      const label = customValue ? 'Financiera' : 'Cliente';
      const iconStyle = { fontSize: 16, color: color };
      const icon = customValue ? (
        <AccountBalance style={iconStyle} />
      ) : (
        <Business style={iconStyle} />
      );
      return (
        <Chip
          icon={icon}
          label={label}
          style={{
            backgroundColor: alpha(color, 0.2),
            color: color,
            fontWeight: 'bold',
            opacity: 1,
            width: '140px',
          }}
        />
      );
    }
    case 'cessionaries':
      if (customValue.length === 0) return '--';
      return customValue[0].cessionary;
    case 'requirements':
      if (!customValue || !Array.isArray(customValue)) return null;
      return (
        <LightTooltip
          title={
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Requerimiento</TableCell>
                  <TableCell>Completado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customValue.map((requirement: Requirement, index: number) => (
                  <TableRow key={index}>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      <Typography
                        style={{ textAlign: 'left', fontWeight: 'bold' }}
                      >
                        {requirement.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography style={{ textAlign: 'center' }}>
                        {requirement.record ? (
                          requirement.record.completed ? (
                            <CheckCircleOutline style={{ color: '#3bc14a' }} />
                          ) : null
                        ) : null}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          }
        >
          <IconButton
            onClick={() => {
              if (handleAction) handleAction(rowId);
            }}
          >
            <Checklist
              style={{
                color: customValue.some(
                  (requirement: Requirement) =>
                    requirement.required && !requirement.record?.completed,
                )
                  ? '#D62839'
                  : '#3bc14a',
              }}
            />
          </IconButton>
        </LightTooltip>
      );
    case 'copyable': {
      const textToCopy = String(customValue ?? '');
      const maxLength = 25;
      const displayText =
        textToCopy.length > maxLength
          ? `${textToCopy.slice(0, maxLength)}...`
          : textToCopy;

      const handleCopy = () => {
        void navigator.clipboard.writeText(textToCopy);
      };

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <LightTooltip
            title={<Typography padding={1}>{textToCopy}</Typography>}
          >
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '150px',
              }}
            >
              {displayText}
            </Typography>
          </LightTooltip>
          <LightTooltip title={<Typography padding={1}>Copiar</Typography>}>
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{
                padding: '2px',
                opacity: 0.6,
                '&:hover': { opacity: 1 },
              }}
            >
              <ContentCopy sx={{ fontSize: 14 }} />
            </IconButton>
          </LightTooltip>
        </Box>
      );
    }
    case 'resell-conciliation-type':
      return (
        <LightTooltip
          title={
            <Typography padding={1}>
              {
                RESSELL_CONCILIATION_TYPES_DESCRIPTIONS[
                  customValue as keyof typeof RESSELL_CONCILIATION_TYPES_DESCRIPTIONS
                ]
              }
            </Typography>
          }
        >
          <Info
            style={{
              fontSize: '16px',
              color: '#666',
            }}
          />
        </LightTooltip>
      );
    default:
      return customValue;
  }
};
